import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Badge,
  SimpleGrid,
  Image,
  Spinner,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FiClock,
  FiCamera,
  FiHeadphones,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
} from "react-icons/fi";
import { formatTimestamp } from "../../../utils/screenCaptureListen";
import {
  fetchScreenListenAssetBlobUrl,
  revokeBlobUrl,
} from "../../../utils/screenListenMedia";

/**
 * @param {{
 *   sessionId?: string|null,
 *   durationLabel?: string,
 *   durationSec?: number|null,
 *   hasAudio?: boolean,
 *   audioUrl?: string|null,
 *   audioMimeType?: string,
 *   frames?: Array<{ elapsedSec: number, text?: string, imageUrl?: string|null }>,
 *   liveFrames?: Array<{ elapsedSec: number, text?: string, previewUrl: string }>,
 *   localAudioUrl?: string|null,
 * }} props
 */
export default function ScreenListenMediaPanel({
  sessionId,
  durationLabel,
  durationSec,
  hasAudio,
  audioUrl,
  audioMimeType = "audio/webm",
  frames = [],
  liveFrames = [],
  localAudioUrl,
}) {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const shotBg = useColorModeValue("gray.100", "gray.700");
  const modalBg = useColorModeValue("gray.900", "black");
  const thumbHover = useColorModeValue("teal.50", "teal.900");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [audioSrc, setAudioSrc] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [frameSrcMap, setFrameSrcMap] = useState({});

  const displayDuration =
    durationLabel ||
    (durationSec != null ? formatTimestamp(durationSec) : null);

  const serverFrames = useMemo(
    () =>
      (frames || [])
        .filter((f) => f?.imageUrl || f?.text)
        .sort((a, b) => a.elapsedSec - b.elapsedSec),
    [frames]
  );

  const liveSorted = useMemo(
    () => [...(liveFrames || [])].sort((a, b) => a.elapsedSec - b.elapsedSec),
    [liveFrames]
  );

  const showLiveGallery = liveSorted.length > 0;
  const showAudio = !!(localAudioUrl || hasAudio || audioUrl);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;

    const load = async () => {
      if (localAudioUrl) {
        setAudioSrc(localAudioUrl);
        setAudioLoading(false);
        return;
      }
      if (!sessionId || !audioUrl || !hasAudio) {
        setAudioSrc(null);
        return;
      }
      setAudioLoading(true);
      try {
        const url = await fetchScreenListenAssetBlobUrl(audioUrl);
        if (cancelled) {
          revokeBlobUrl(url);
          return;
        }
        createdUrl = url;
        setAudioSrc(url);
      } catch {
        if (!cancelled) setAudioSrc(null);
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl && createdUrl !== localAudioUrl) {
        revokeBlobUrl(createdUrl);
      }
    };
  }, [sessionId, audioUrl, hasAudio, localAudioUrl]);

  useEffect(() => {
    if (!sessionId || showLiveGallery) {
      setFrameSrcMap({});
      return;
    }

    let cancelled = false;
    const created = [];

    const loadFrames = async () => {
      const next = {};
      for (const frame of serverFrames) {
        if (!frame.imageUrl || cancelled) continue;
        try {
          const url = await fetchScreenListenAssetBlobUrl(frame.imageUrl);
          created.push(url);
          next[frame.elapsedSec] = url;
        } catch {
          /* skip */
        }
      }
      if (!cancelled) setFrameSrcMap(next);
    };

    loadFrames();
    return () => {
      cancelled = true;
      created.forEach(revokeBlobUrl);
    };
  }, [sessionId, serverFrames, showLiveGallery]);

  const screenshotItems = useMemo(() => {
    if (showLiveGallery) {
      return liveSorted.map((f) => ({
        key: `live-${f.elapsedSec}`,
        elapsedSec: f.elapsedSec,
        text: f.text,
        src: f.previewUrl,
      }));
    }
    return serverFrames
      .filter((f) => frameSrcMap[f.elapsedSec] || f.text)
      .map((f) => ({
        key: `srv-${f.elapsedSec}`,
        elapsedSec: f.elapsedSec,
        text: f.text,
        src: frameSrcMap[f.elapsedSec] || null,
      }));
  }, [showLiveGallery, liveSorted, serverFrames, frameSrcMap]);

  const viewableItems = useMemo(
    () => screenshotItems.filter((item) => item.src),
    [screenshotItems]
  );

  const openLightbox = useCallback(
    (item) => {
      const idx = viewableItems.findIndex((v) => v.key === item.key);
      if (idx < 0) return;
      setLightboxIndex(idx);
      onOpen();
    },
    [viewableItems, onOpen]
  );

  const goPrev = useCallback(() => {
    setLightboxIndex((i) =>
      viewableItems.length ? (i - 1 + viewableItems.length) % viewableItems.length : 0
    );
  }, [viewableItems.length]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) =>
      viewableItems.length ? (i + 1) % viewableItems.length : 0
    );
  }, [viewableItems.length]);

  const activeItem = viewableItems[lightboxIndex] || null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, goPrev, goNext]);

  if (!showAudio && screenshotItems.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        bg={cardBg}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        p={{ base: 4, md: 6 }}
        boxShadow="sm"
      >
        <HStack justify="space-between" mb={4} flexWrap="wrap">
          <HStack spacing={2}>
            <Icon as={FiCamera} color="teal.500" />
            <Heading size="md">Recording & screenshots</Heading>
          </HStack>
          {displayDuration ? (
            <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
              <Icon as={FiClock} mr={1} />
              {displayDuration}
            </Badge>
          ) : null}
        </HStack>

        {showAudio ? (
          <Box mb={screenshotItems.length ? 5 : 0}>
            <HStack mb={2} spacing={2}>
              <Icon as={FiHeadphones} color="purple.500" />
              <Text fontSize="sm" fontWeight="semibold">
                Full recording
              </Text>
            </HStack>
            {audioLoading ? (
              <HStack py={4}>
                <Spinner size="sm" color="teal.500" />
                <Text fontSize="sm" color={muted}>
                  Loading audio…
                </Text>
              </HStack>
            ) : audioSrc ? (
              <Box
                as="audio"
                controls
                src={audioSrc}
                w="100%"
                sx={{ maxHeight: "48px" }}
                preload="metadata"
              />
            ) : (
              <Text fontSize="sm" color={muted}>
                Audio not available for this session.
              </Text>
            )}
            {audioMimeType && audioSrc ? (
              <Text fontSize="xs" color={muted} mt={1}>
                Use the player above to hear the complete capture.
              </Text>
            ) : null}
          </Box>
        ) : null}

        {screenshotItems.length > 0 ? (
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="semibold">
                Screenshots ({screenshotItems.length})
              </Text>
              {viewableItems.length > 0 ? (
                <Text fontSize="xs" color={muted}>
                  Click any image to view full size
                </Text>
              ) : null}
            </HStack>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
              {screenshotItems.map((item) => (
                <ScreenshotThumb
                  key={item.key}
                  item={item}
                  borderColor={borderColor}
                  shotBg={shotBg}
                  thumbHover={thumbHover}
                  muted={muted}
                  onOpen={openLightbox}
                />
              ))}
            </SimpleGrid>
          </VStack>
        ) : null}
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent
          bg={modalBg}
          maxW={{ base: "100vw", md: "95vw" }}
          maxH={{ base: "100vh", md: "95vh" }}
          m={{ base: 0, md: 4 }}
        >
          <ModalHeader color="white" py={3} pr={12}>
            <HStack spacing={3} flexWrap="wrap">
              <Badge colorScheme="cyan" fontSize="sm">
                {activeItem
                  ? formatTimestamp(activeItem.elapsedSec)
                  : "Screenshot"}
              </Badge>
              {viewableItems.length > 1 ? (
                <Text fontSize="sm" fontWeight="normal" opacity={0.85}>
                  {lightboxIndex + 1} of {viewableItems.length}
                </Text>
              ) : null}
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6} px={{ base: 2, md: 6 }} position="relative">
            {activeItem?.src ? (
              <Box position="relative">
                {viewableItems.length > 1 ? (
                  <>
                    <IconButton
                      aria-label="Previous screenshot"
                      icon={<FiChevronLeft />}
                      position="absolute"
                      left={{ base: 0, md: -2 }}
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      onClick={goPrev}
                      colorScheme="whiteAlpha"
                      size="lg"
                      borderRadius="full"
                    />
                    <IconButton
                      aria-label="Next screenshot"
                      icon={<FiChevronRight />}
                      position="absolute"
                      right={{ base: 0, md: -2 }}
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                      onClick={goNext}
                      colorScheme="whiteAlpha"
                      size="lg"
                      borderRadius="full"
                    />
                  </>
                ) : null}
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minH={{ base: "50vh", md: "60vh" }}
                  maxH="75vh"
                  w="100%"
                >
                  <Image
                    src={activeItem.src}
                    alt={`Screenshot at ${formatTimestamp(activeItem.elapsedSec)}`}
                    maxH="75vh"
                    maxW="100%"
                    objectFit="contain"
                    borderRadius="md"
                  />
                </Box>
                {activeItem.text ? (
                  <Text
                    fontSize="sm"
                    color="whiteAlpha.900"
                    mt={4}
                    lineHeight="tall"
                    whiteSpace="pre-wrap"
                  >
                    {activeItem.text}
                  </Text>
                ) : null}
                {viewableItems.length > 1 ? (
                  <Text
                    fontSize="xs"
                    color="whiteAlpha.700"
                    textAlign="center"
                    mt={3}
                  >
                    Use arrow keys or side buttons to move between screenshots
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function ScreenshotThumb({ item, borderColor, shotBg, thumbHover, muted, onOpen }) {
  const canOpen = !!item.src;

  return (
    <Box
      borderRadius="lg"
      overflow="hidden"
      borderWidth="1px"
      borderColor={borderColor}
      bg={shotBg}
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      cursor={canOpen ? "pointer" : "default"}
      transition="box-shadow 0.15s, transform 0.15s"
      _hover={
        canOpen
          ? { bg: thumbHover, boxShadow: "md", transform: "scale(1.02)" }
          : undefined
      }
      onClick={() => canOpen && onOpen(item)}
      onKeyDown={(e) => {
        if (canOpen && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen(item);
        }
      }}
      title={canOpen ? "Click to view full size" : undefined}
    >
      {item.src ? (
        <Box position="relative">
          <Image
            src={item.src}
            alt={`Screenshot at ${formatTimestamp(item.elapsedSec)}`}
            w="100%"
            h="120px"
            objectFit="cover"
          />
          <HStack
            position="absolute"
            bottom={1}
            right={1}
            bg="blackAlpha.700"
            color="white"
            px={2}
            py={0.5}
            borderRadius="md"
            spacing={1}
            fontSize="xs"
          >
            <Icon as={FiMaximize2} boxSize={3} />
            <Text>Enlarge</Text>
          </HStack>
        </Box>
      ) : (
        <FlexPlaceholder />
      )}
      <Box p={2}>
        <Badge colorScheme="cyan" fontSize="xs">
          {formatTimestamp(item.elapsedSec)}
        </Badge>
        {item.text ? (
          <Text fontSize="xs" color={muted} noOfLines={2} mt={1}>
            {item.text}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

function FlexPlaceholder() {
  return (
    <Box h="120px" display="flex" alignItems="center" justifyContent="center">
      <Spinner size="sm" />
    </Box>
  );
}
